/**
 * Utilidades para manejo de tokens JWT
 */

/**
 * Decodificar un token JWT sin verificar la firma
 * @param {string} token - Token JWT
 * @returns {object|null} - Payload decodificado o null si hay error
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null;

    // El token JWT tiene 3 partes separadas por puntos: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decodificar la segunda parte (payload) que está en Base64URL
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Verificar si un token está expirado
 * @param {string} token - Token JWT
 * @returns {boolean} - true si está expirado, false si no
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  // exp viene en segundos, Date.now() está en milisegundos
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();

  return currentTime >= expirationTime;
};

/**
 * Verificar si un token está próximo a expirar (dentro de los próximos 5 minutos)
 * @param {string} token - Token JWT
 * @param {number} minutesBeforeExpiry - Minutos antes de expiración (default: 5)
 * @returns {boolean} - true si está por expirar, false si no
 */
export const isTokenExpiringSoon = (token, minutesBeforeExpiry = 5) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const bufferTime = minutesBeforeExpiry * 60 * 1000; // Convertir minutos a milisegundos

  // Si falta menos del buffer time para expirar, retornar true
  return (expirationTime - currentTime) <= bufferTime;
};

/**
 * Obtener tiempo restante hasta la expiración en milisegundos
 * @param {string} token - Token JWT
 * @returns {number} - Milisegundos hasta expiración, o 0 si ya expiró
 */
export const getTimeUntilExpiry = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return 0;

  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const timeRemaining = expirationTime - currentTime;

  return timeRemaining > 0 ? timeRemaining : 0;
};

/**
 * Obtener el ID de usuario del token
 * @param {string} token - Token JWT
 * @returns {string|null} - ID de usuario o null
 */
export const getUserIdFromToken = (token) => {
  const decoded = decodeToken(token);
  return decoded?.sub || null;
};
